import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Wrench, Plus, Loader2, CheckCircle } from "lucide-react";

const TIPOS = [
  { value: "preventivo", label: "Preventivo" },
  { value: "correctivo", label: "Correctivo" },
  { value: "limpieza", label: "Limpieza" }
];

export default function MobiliarioMantenimientoPage() {
  const [registros, setRegistros] = useState([]);
  const [activos, setActivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ activo_id: "", tipo: "preventivo", descripcion: "", costo: 0, fecha_programada: "", proveedor: "", observaciones: "" });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [regRes, activosRes] = await Promise.all([
        axios.get(`${API}/mobiliario/mantenimiento`, getAuthHeaders()),
        axios.get(`${API}/mobiliario/activos`, getAuthHeaders())
      ]);
      setRegistros(regRes.data || []);
      setActivos(activosRes.data || []);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { 
    setFormData({ activo_id: "", tipo: "preventivo", descripcion: "", costo: 0, fecha_programada: new Date().toISOString().split('T')[0], proveedor: "", observaciones: "" }); 
    setShowDialog(true); 
  };

  const handleSubmit = async () => {
    if (!formData.activo_id || !formData.descripcion) { toast.error("Activo y descripción requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mobiliario/mantenimiento`, formData, getAuthHeaders());
      toast.success("Mantenimiento programado");
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleCompletar = async (id) => {
    if (!confirm("¿Marcar como completado?")) return;
    try {
      await axios.put(`${API}/mobiliario/mantenimiento/${id}/completar`, {}, getAuthHeaders());
      toast.success("Mantenimiento completado"); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900"><Sidebar /><main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></main></div>;

  const pendientes = registros.filter(r => !r.fecha_realizado);
  const completados = registros.filter(r => r.fecha_realizado);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="mobiliario-mantenimiento-page">
        <div className="flex justify-between mb-6">
          <div><h1 className="text-3xl font-bold dark:text-white">Mantenimiento de Mobiliario</h1><p className="text-slate-500">{pendientes.length} pendientes</p></div>
          <Button onClick={openNew} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"><Plus className="w-4 h-4 mr-2" />Nuevo</Button>
        </div>

        {pendientes.length > 0 && (
          <Card className="mb-6 border-amber-200">
            <CardContent className="p-4">
              <h3 className="font-medium text-amber-800 mb-4">Mantenimientos Pendientes</h3>
              <div className="space-y-3">
                {pendientes.map(reg => (
                  <div key={reg.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div>
                      <p className="font-medium">{reg.activo_nombre} ({reg.activo_codigo})</p>
                      <p className="text-sm text-slate-600">{reg.descripcion}</p>
                      <Badge variant="outline" className="mt-1">{TIPOS.find(t => t.value === reg.tipo)?.label || reg.tipo}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Programado: {reg.fecha_programada}</p>
                      <Button size="sm" className="mt-2" onClick={() => handleCompletar(reg.id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Completar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-slate-50 dark:bg-slate-800">
                  <th className="text-left p-4 font-medium text-slate-600">Activo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Tipo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Descripción</th>
                  <th className="text-left p-4 font-medium text-slate-600">Fecha</th>
                  <th className="text-left p-4 font-medium text-slate-600">Costo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Estado</th>
                </tr>
              </thead>
              <tbody>
                {registros.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-slate-500">No hay registros</td></tr>
                ) : registros.map(reg => (
                  <tr key={reg.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="p-4"><p className="font-medium dark:text-white">{reg.activo_nombre}</p><p className="text-sm text-slate-500">{reg.activo_codigo}</p></td>
                    <td className="p-4"><Badge variant="outline">{TIPOS.find(t => t.value === reg.tipo)?.label || reg.tipo}</Badge></td>
                    <td className="p-4 text-slate-600 text-sm max-w-xs truncate">{reg.descripcion}</td>
                    <td className="p-4 text-slate-600 text-sm">{reg.fecha_realizado || reg.fecha_programada}</td>
                    <td className="p-4 text-slate-600">RD$ {reg.costo?.toLocaleString() || 0}</td>
                    <td className="p-4">{reg.fecha_realizado ? <Badge className="bg-green-100 text-green-700">Completado</Badge> : <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle><Wrench className="w-5 h-5 inline mr-2" />Programar Mantenimiento</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Activo *</Label>
                <Select value={formData.activo_id} onValueChange={v => setFormData({...formData, activo_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar activo..." /></SelectTrigger>
                  <SelectContent>
                    {activos.filter(a => a.estado !== "baja").map(a => <SelectItem key={a.id} value={a.id}>{a.codigo} - {a.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={formData.tipo} onValueChange={v => setFormData({...formData, tipo: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha Programada</Label>
                  <Input type="date" value={formData.fecha_programada} onChange={e => setFormData({...formData, fecha_programada: e.target.value})} />
                </div>
              </div>
              <div><Label>Descripción *</Label><Textarea value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} placeholder="Describir el mantenimiento..." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Costo Estimado</Label><Input type="number" value={formData.costo} onChange={e => setFormData({...formData, costo: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Proveedor</Label><Input value={formData.proveedor} onChange={e => setFormData({...formData, proveedor: e.target.value})} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Programar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
